import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { NestedError } from "abweb/dto/exception/NestedError";
/** Hold server-side exception, as well as the larger response context */
export class RemoteError extends NestedError {
  // Api follows NestedError convention:
  // remoteError.message: only displays errorMsg
  // remoteError.toString(): causal chain of message:  errorMsg (+ causedBy.toString())(+ remoteEx.chainedMessages)
  // remoteError.stack: causal chain of message:  stack (+ this.causedBy.recursiveStack) (+ remoteEx.stack)

  constructor(errorMsg, causedBy, response, remoteException) {
    super(errorMsg, causedBy);
    _defineProperty(this, "response", void 0);
    _defineProperty(this, "remoteException", void 0);
    this.response = response;
    this.remoteException = remoteException;
    if (this.remoteException) {
      // remoteEx.toString prints out jsonified stack
      this.stack += NestedError.causedByPrefix + this.remoteException.toString();
    }
  }

  // Adds message chaining for both the causedBy stack followed by the remoteEx stack (jsonified)
  toString() {
    return this.inNestedErrorConstructor || !this.remoteException ? super.toString() : super.toString() // errorMsg (+ causedBy + causedBy.toString()) (+ causedBy + this.remoteEx.chainedMessage)
    + NestedError.causedByPrefix + this.remoteException.chainedMessage;
  }
  get isAuthenticationError() {
    return this.remoteException != null && this.remoteException.messageCode === "authorization.notAuthorized" && this.remoteException.messageArgs != null && this.remoteException.messageArgs[0] === "guest";
  }
  get isAuthenticationServiceMisconfiguredError() {
    return this.remoteException != null && this.remoteException.messageCode === "login.authenticationMisconfiguredMessage";
  }

  /**
   * Returns true if the RemoteError represents an authorization error that indicates there is no session.
   * @returns {boolean}
   */
  get isNoSessionError() {
    return this.remoteException?.messageCode === "authorization.noSession";
  }
  get isStolenSessionError() {
    // abweb.error.noSession thrown when clientId is not found in user's current session, indicating
    // that it is the wrong user session (user logged in via a different browser tab that clobbered
    // this browser tab's jsessionid, so clientId not found in user session). See CAT-1660 for more details.
    // Also thrown by mhub Load Balancer failover workflow (migrates session to new appserver), see MH-22220.
    return this.remoteException?.messageCode === "abweb.error.noSession";
  }

  /**
   * Returns true if the RemoteError represents a session that is invalid because of concurrency.
   * @returns {boolean}
   */
  get isConcurrentSessionInvalid() {
    return this.remoteException?.messageCode === "concurrency.expiredSession.invalidated";
  }

  /**
   * Returns true if the RemoteError represents a session that is disabled because of concurrency.
   * @returns {boolean}
   */
  get isConcurrentSessionDisabled() {
    return this.remoteException?.messageCode === "concurrency.expiredSession";
  }

  /**
   * Returns true if the RemoteError represents an authorization error that indicates there is no session, and the
   * application needs to allow the user to log into the SSO identity provider.
   * @returns {boolean}
   */
  get isSsoNoSessionError() {
    return this.isNoSessionError && this.remoteException != null && this.remoteException.messageArgs != null && this.remoteException.messageArgs.length > 0 && this.remoteException.messageArgs[0] === "sso";
  }

  /**
   * Returns true if the RemoteError represents an authorization error that indicates there is no session, and the
   * application is responsible for prompting the user to log in.
   * @returns {boolean}
   */
  get isLocalNoSessionError() {
    return this.isNoSessionError && this.remoteException != null && this.remoteException.messageArgs != null && this.remoteException.messageArgs.length > 0 && this.remoteException.messageArgs[0] === "application";
  }
}
//# sourceMappingURL=RemoteError.js.map
